import { MenuItem } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Control, UseFormWatch } from 'react-hook-form';
import { useParams } from 'react-router';

import { BanksByCountrySelect } from '@/components/molecules/BanksByCountrySelect';
import { OneOf } from '@/components/molecules/OneOf';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { FormWithErrorsRegister } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import type { paymentMethodFiatSchema } from '@/schemas/paymentMethod';
import { BankAccountType } from '@/types/bankAccountType';
import { OneOfOption } from '@/types/oneOfOption';

type PaymentMethodForm = typeof paymentMethodFiatSchema.infer;

interface Props {
  readonly control: Control<PaymentMethodForm>;
  readonly register: FormWithErrorsRegister<PaymentMethodForm>;
  readonly watch: UseFormWatch<PaymentMethodForm>;
}

export const COPAccountDetailsFields: React.FC<Props> = ({
  register,
  watch,
  control,
}: Props): React.ReactElement => {
  const country = watch('country');
  const tr = useTranslator();
  const { contactId = '' } = useParams();
  const recipientType = watch('recipientType');

  const bankAccountOptions = React.useMemo(
    (): readonly OneOfOption[] => [
      {
        label: tr('CHECKING'),
        value: BankAccountType.checking,
      },
      {
        label: tr('SAVINGS'),
        value: BankAccountType.savings,
      },
    ],
    [tr],
  );

  const recipientTypeOptions = React.useMemo(
    (): readonly OneOfOption[] => [
      {
        label: tr('INDIVIDUAL'),
        value: 'individual',
      },
      {
        label: tr('COMPANY'),
        value: 'company',
      },
    ],
    [tr],
  );

  return (
    <Box display="flex" flexDirection="column" gap={5}>
      <ControllerWithError
        name="recipientType"
        label={tr('RECIPIENT_TYPE')}
        control={control}
        render={({
          field,
        }: ControllerWithErrorParam<PaymentMethodForm, 'recipientType'>): React.ReactElement => (
          <OneOf options={recipientTypeOptions} {...field} />
        )}
      />

      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body2" color="text.primary">
          {tr('RECIPIENT_INFORMATION')}
        </Typography>
        <Box display="flex" gap={2}>
          <TextField
            fullWidth={true}
            {...register({
              name: 'firstName',
              label: recipientType === 'individual' ? tr('FIRST_NAME') : tr('LEGAL_ENTITY_NAME'),
            })}
          />
          {recipientType === 'individual' ? (
            <TextField
              fullWidth={true}
              {...register({ name: 'lastName', label: tr('LAST_NAME') })}
            />
          ) : null}
        </Box>
      </Box>
      <OneOf
        options={bankAccountOptions}
        {...register({ name: 'bankType', label: tr('BANK_ACCOUNT_TYPE') })}
      />
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body2" color="text.primary">
          {tr('BANK_ACCOUNT_INFORMATION')}
        </Typography>
        <Box display="flex" gap={2}>
          <TextField
            fullWidth={true}
            {...register({ name: 'bankAccount', label: tr('BANK_ACCOUNT_NUMBER') })}
          />
          <BanksByCountrySelect
            countryCode={country}
            fullWidth={true}
            {...register({ name: 'bankCode', label: tr('BANK') })}
          />
        </Box>

        {contactId !== '' ? (
          <>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth={true}
                select={true}
                {...register({ name: 'documentType', label: tr('RECIPIENT_ID_TYPE') })}>
                <MenuItem value="CC">{tr('CO_CC_LABEL')}</MenuItem>
                <MenuItem value="CE">{tr('CO_CE_LABEL')}</MenuItem>
                <MenuItem value="NIT">{tr('CO_NIT_LABEL')}</MenuItem>
                <MenuItem value="PASS">{tr('PASSPORT')}</MenuItem>
                <MenuItem value="PEP">{tr('CO_SPECIAL_RESIDENCE_PERMIT')}</MenuItem>
              </TextField>
              <TextField
                fullWidth={true}
                {...register({ name: 'documentId', label: tr('RECIPIENT_ID') })}
              />
            </Box>
            <TextField
              fullWidth={true}
              {...register({ name: 'email', label: tr('RECIPIENT_EMAIL') })}
            />
          </>
        ) : null}
      </Box>
    </Box>
  );
};
